import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  collection, addDoc, getDocs, doc, getDoc, setDoc,
  query, where, serverTimestamp, updateDoc, writeBatch, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { PLANS, PLAN_PRICES, CURRENCIES, DEFAULT_POINT_RULES, FREE_POINT_RULES, POINT_LABELS } from '../utils/constants';
import BottomNav from '../components/BottomNav';
import { useTranslation } from 'react-i18next';
import ModalPago from '../components/ModalPago';

// ── SOUND ─────────────────────────────────────────────────
function playGolSound(volume = 0.8) {
  const muted = localStorage.getItem('pitazo_muted') === 'true';
  if (muted) return;
  try {
    const audio = new Audio('/sounds/gol.mp3');
    audio.volume = volume;
    audio.play().catch(() => {});
  } catch(e) {}
}

// ── ESTILOS BASE ──────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { overflow-x: hidden !important; width: 100%; }
  body { overflow-x: hidden !important; width: 100%; max-width: 100vw; background: #080810; color: #F5F5F0; font-family: 'Oswald','Arial Black',sans-serif; }
  #root { overflow-x: hidden; width: 100%; max-width: 100vw; }
  ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #14141f; } ::-webkit-scrollbar-thumb { background: #F0B429; border-radius: 2px; }
  input, textarea, select { outline: none; }
  input::placeholder { color: #555; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  input[type="number"] { -moz-appearance: textfield; }
`;

const colors = { gold: '#F0B429', green: '#06B86A', red: '#E63946', bg: '#080810', card: '#14141f', border: 'rgba(255,255,255,0.07)' };

const btn = {
  gold: { background: 'linear-gradient(90deg,#F0B429,#C9820A)', border: 'none', borderRadius: 10, padding: '12px 20px', fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 600, color: '#080810', letterSpacing: 2, cursor: 'pointer' },
  outline: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 16px', fontFamily: "'Oswald',sans-serif", fontSize: 13, color: '#F5F5F0', letterSpacing: 1, cursor: 'pointer' },
  ghost: { background: 'none', border: 'none', fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B', cursor: 'pointer' },
  red: { background: 'rgba(230,57,70,0.1)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 8, padding: '6px 12px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#E63946', cursor: 'pointer' }
};

function Card({ children, style = {} }) {
  return <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 16, ...style }}>{children}</div>;
}

function SectionTitle({ children }) {
  return <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 12, letterSpacing: 2, color: '#6B6B7B', padding: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
    {children}<span style={{ flex: 1, height: 1, background: colors.border }} />
  </div>;
}

function Toast({ msg, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 2800); return () => clearTimeout(t); }, []);
  return <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: colors.green, color: '#fff', padding: '11px 22px', borderRadius: 20, fontFamily: "'Inter',sans-serif", fontSize: 13, fontWeight: 600, zIndex: 300, whiteSpace: 'nowrap', animation: 'fadeIn .3s ease' }}>{msg}</div>;
}

// ── MAIN ADMIN PAGE ───────────────────────────────────────
export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser, userData, logout } = useAuth();
  const [tab, setTab] = useState('groups');
  const [myGroups, setMyGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => setToast(msg);

  // Fix: evitar scroll horizontal en WebView de Capacitor
  useEffect(() => {
    const preventHorizontalScroll = () => {
      if (window.scrollX !== 0) window.scrollTo(0, window.scrollY);
    };
    window.addEventListener('scroll', preventHorizontalScroll, { passive: true });
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => window.removeEventListener('scroll', preventHorizontalScroll);
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/admin-login'); return; }
    loadMyGroups();
  }, [currentUser]);

  

  async function loadMyGroups() {
    setLoading(true);
    try {
      const q = query(collection(db, 'groups'), where('adminId', '==', currentUser.uid));
      const snap = await getDocs(q);
      let groups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      if (groups.length === 0 && userData?.email) {
        const qByEmail = query(collection(db, 'groups'), where('adminEmail', '==', userData.email));
        const snapByEmail = await getDocs(qByEmail);
        const groupsToFix = [];
        
        snapByEmail.forEach(docSnap => {
          const data = docSnap.data();
          if (data.adminId !== currentUser.uid) {
            groupsToFix.push({ id: docSnap.id, ref: doc(db, 'groups', docSnap.id), data: data });
          }
        });
        
        if (groupsToFix.length > 0) {
          for (const group of groupsToFix) {
            await updateDoc(group.ref, {
              adminId: currentUser.uid,
              adminName: userData?.name || currentUser.displayName || group.data.adminName,
              _fixedAt: serverTimestamp()
            });
          }
          const snapFixed = await getDocs(q);
          groups = snapFixed.docs.map(d => ({ id: d.id, ...d.data() }));
        }
      }
      
      setMyGroups(groups);
      if (groups.length > 0) {
        const newGroupId = location?.state?.newGroupId;
        if (newGroupId) {
          const found = groups.find(g => g.id === newGroupId);
          if (found) { setSelectedGroup(found); return; }
        }
        if (!selectedGroup) {
          const activeId = localStorage.getItem('activeGroupId');
          const activeGroup = activeId ? groups.find(g => g.id === activeId) : null;
          setSelectedGroup(activeGroup || groups[0]);
        }
      }
    } catch (e) { 
      console.error('Error cargando grupos:', e);
    }
    setLoading(false);
  }

  async function handleLogout() {
    await logout();
    navigate('/mis-grupos');
  }

  // NUEVA FUNCIÓN PARA ELIMINAR GRUPOS BASCURA
  async function handleDeleteGroup(groupId) {
    if (window.confirm('¿Estás seguro de eliminar este grupo por completo? Esta acción no se puede deshacer.')) {
      try {
        await deleteDoc(doc(db, 'groups', groupId));
        showToast(t('toast_group_deleted'));
        setSelectedGroup(null);
        localStorage.removeItem('activeGroupId');
        loadMyGroups();
      } catch (e) {
        showToast(t('toast_error_delete_group'));
        console.error(e);
      }
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, maxWidth: 480, width: '100%', margin: '0 auto', position: 'relative', overflowX: 'hidden' }}>
      <style>{CSS}</style>
      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}

      {/* HEADER */}
      <div style={{ padding: '50px 16px 12px', background: 'linear-gradient(180deg,#0f0f1a,transparent)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 3 }}>
            PITAZO<span style={{ color: colors.gold }}>MUNDIAL</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {userData?.avatar && <img src={userData.avatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%' }} />}
            <button onClick={() => navigate('/mis-grupos')}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 10px', fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#8B8B9B', cursor: 'pointer', letterSpacing: 1, whiteSpace: 'nowrap' }}>
                ← MIS GRUPOS
              </button>
            
          {/* SELECTOR DE IDIOMAS */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          </div>
          <button style={btn.ghost} onClick={handleLogout}>Salir</button>
          </div>
        </div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#6B6B7B' }}>
          👑 Panel de Admin · {userData?.name}
        </div>
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTop: `3px solid ${colors.gold}`, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
            <div style={{ fontFamily: "'Inter',sans-serif", color: '#6B6B7B', fontSize: 13 }}>Cargando tus grupos...</div>
          </div>
        ) : myGroups.length === 0 ? (
          <NoGroupsView onCreateClick={() => navigate('/crear-grupo')} />
        ) : (
          <>
            {selectedGroup && (
              <GroupPanel 
                group={selectedGroup} 
                showToast={showToast} 
                onRefresh={loadMyGroups} 
                currentUser={currentUser} 
                onDeleteGroup={() => handleDeleteGroup(selectedGroup.id)} // Pasamos la función de eliminar
              />
            )}
          </>
        )}

        {/* BOTÓN CREAR NUEVO GRUPO */}
        <div style={{ marginTop: 16 }}>
          <button style={{ ...btn.gold, width: '100%', padding: 14, fontSize: 15 }} onClick={() => navigate('/crear-grupo')}>
            + CREAR NUEVO GRUPO
          </button>
        </div>
      </div>
    </div>
  );
}

// ── NO GROUPS VIEW ────────────────────────────────────────
function NoGroupsView({ onCreateClick }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>🏆</div>
      <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, letterSpacing: 2, marginBottom: 8 }}>AÚN NO TIENES GRUPOS</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B', marginBottom: 24 }}>Crea tu primera polla del Mundial 2026</div>
      <button style={{ ...btn.gold, padding: '14px 28px', fontSize: 15 }} onClick={onCreateClick}>CREAR MI PRIMER GRUPO</button>
    </div>
  );
}

// ── GROUP PANEL ───────────────────────────────────────────
function GroupPanel({ group, showToast, onRefresh, currentUser, onDeleteGroup }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const [members, setMembers] = useState([]);
  const [links, setLinks] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [showModalPago, setShowModalPago] = useState(false);
  const isPending = group.status === 'pending';
  const [pagoAction, setPagoAction] = useState(null); // ← NUEVO: 'complete' o 'upgrade'

  useEffect(() => { loadGroupData(); }, [group.id]);

  async function loadGroupData() {
    setLoadingData(true);
    try {
      const membersSnap = await getDocs(collection(db, 'groups', group.id, 'members'));
      setMembers(membersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const linksQ = query(collection(db, 'inviteLinks'), where('groupId', '==', group.id));
      const linksSnap = await getDocs(linksQ);
      setLinks(linksSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoadingData(false);
  }

  const plan = PLANS.find(p => p.id === group.plan) || PLANS[0];
  const spotsUsed = members.length;
  const spotsLeft = plan.maxMembers - spotsUsed;

  return (
    <div style={{ animation: 'fadeIn .3s ease', overflow: 'hidden' }}>
      {/* Group header */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, letterSpacing: 1, marginBottom: 2 }}>{group.name}</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>
              Plan {plan.name} · {group.status === 'active' ? '🟢 Activo' : '⏳ Pendiente'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, color: colors.gold }}>{spotsUsed}/{plan.maxMembers}</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B' }}>CUPOS USADOS</div>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${(spotsUsed / plan.maxMembers) * 100}%`, background: spotsLeft <= 2 ? colors.red : colors.gold, borderRadius: 2, transition: 'width .5s ease' }} />
        </div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: spotsLeft <= 2 ? colors.red : '#6B6B7B', marginTop: 4 }}>
          {spotsLeft > 0 ? `${spotsLeft} cupos disponibles` : '🚫 Grupo lleno'}
        </div>

        {/* Botón mejorar plan */}
        {group.plan === 'free' || group.plan === 'starter' || group.plan === 'grupo' || group.plan === 'empresa' || group.plan === 'grande' ? (
          <button
            onClick={() => {
              setPagoAction('upgrade');
              setShowModalPago(true);
             }}
            style={{
              marginTop: 12, width: '100%', padding: '10px',
              background: 'rgba(240,180,41,0.08)',
              border: '1px solid rgba(240,180,41,0.25)',
              borderRadius: 10, cursor: 'pointer',
              fontFamily: "'Oswald',sans-serif", fontSize: 13,
              color: colors.gold, letterSpacing: 1,
            }}>
            ⚡ MEJORAR PLAN — MÁS CUPOS
          </button>
        ) : null}
      </Card>

      {/* Banner pago pendiente */}
      {isPending && (
        <div style={{ margin: '12px 0', padding: '14px 16px', background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.3)', borderRadius: 12 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, color: '#E63946', letterSpacing: 1, marginBottom: 6 }}>⚠️ PAGO PENDIENTE</div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#aaa', marginBottom: 10 }}>
            Este grupo está bloqueado hasta que confirmes el pago. No podés generar links ni invitar participantes.
          </div>
          <button onClick={() => {
  setPagoAction('complete');
  setShowModalPago(true);
}} style={{ padding: '10px 16px', background: 'linear-gradient(90deg,#F0B429,#C9820A)', border: 'none', borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 1, color: '#080810', cursor: 'pointer' }}>
            💳 COMPLETAR PAGO
          </button>
        </div>
      )}

      {tab === 'overview' && <OverviewTab group={group} members={members} plan={plan} links={links} onDeleteGroup={onDeleteGroup} />}
      {tab === 'members' && <MembersTab members={members} loadingData={loadingData} group={group} />}
      {tab === 'links' && (
        isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: '#E63946', marginBottom: 8 }}>SECCIÓN BLOQUEADA</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#888', marginBottom: 16 }}>Completá el pago para poder invitar participantes.</div>
            <button onClick={() => {
  setPagoAction('complete');
  setShowModalPago(true);
}} style={{ padding: '12px 20px', background: 'linear-gradient(90deg,#F0B429,#C9820A)', border: 'none', borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, color: '#080810', cursor: 'pointer' }}>💳 COMPLETAR PAGO</button>
          </div>
        ) : <LinksTab group={group} links={links} plan={plan} spotsLeft={spotsLeft} onLinksGenerated={() => { loadGroupData(); showToast(t('toast_links_generated')); }} showToast={showToast} />
      )}
      {tab === 'points' && (
        isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: '#E63946', marginBottom: 8 }}>SECCIÓN BLOQUEADA</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#888', marginBottom: 16 }}>Completá el pago para personalizar los puntos.</div>
            <button onClick={() => {
  setPagoAction('complete');
  setShowModalPago(true);
}} style={{ padding: '12px 20px', background: 'linear-gradient(90deg,#F0B429,#C9820A)', border: 'none', borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, color: '#080810', cursor: 'pointer' }}>💳 COMPLETAR PAGO</button>
          </div>
        ) : <PointsTab group={group} showToast={showToast} onRefresh={onRefresh} />
      )}
      {tab === 'matches' && (
        isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: '#E63946', marginBottom: 8 }}>SECCIÓN BLOQUEADA</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#888', marginBottom: 16 }}>Completá el pago para ver y gestionar los partidos.</div>
            <button onClick={() => {
  setPagoAction('complete');
  setShowModalPago(true);
}} style={{ padding: '12px 20px', background: 'linear-gradient(90deg,#F0B429,#C9820A)', border: 'none', borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 14, fontWeight: 700, color: '#080810', cursor: 'pointer' }}>💳 COMPLETAR PAGO</button>
          </div>
        ) : <MatchesAdminTab group={group} members={members} showToast={showToast} />
      )}

      <BottomNav
        tabs={[
          { id: 'overview', label: t('tab_overview'),  icon: '📊' },
          { id: 'members',  label: t('tab_members'), icon: '👥' },
          { id: 'links',    label: t('tab_invite'),   icon: '📲' },
          { id: 'points',   label: t('tab_points'),    icon: '⚙️' },
          { id: 'matches',  label: t('tab_matches'),  icon: '⚽' },
        ]}
        active={tab}
        onChange={setTab}
      />

            {/* Modal de pago para mejorar plan */}
      {showModalPago && (() => {
  // Determinar el plan a pagar según la acción guardada en pagoAction
  let targetPlan = null;
  const planOrder = ['free', 'starter', 'grupo', 'empresa', 'grande', 'masivo'];
  const currentIndex = planOrder.indexOf(group.plan);
  
  if (pagoAction === 'complete') {
    // Completar pago: pagar el plan actual del grupo
    if (group.plan === 'free') {
      targetPlan = PLANS.find(p => p.id === 'starter');
    } else {
      targetPlan = PLANS.find(p => p.id === group.plan);
    }
  } else if (pagoAction === 'upgrade') {
    // Mejorar plan: siguiente nivel
    const nextPlanId = planOrder[currentIndex + 1];
    if (nextPlanId) {
      targetPlan = PLANS.find(p => p.id === nextPlanId);
    } else {
      // Ya es el plan más alto
      showToast('Ya tienes el plan más alto disponible');
      setShowModalPago(false);
      setPagoAction(null);
      return null;
    }
  }
  
  // Fallback por si algo sale mal
  if (!targetPlan) {
    targetPlan = PLANS.find(p => p.id === 'starter');
  }
  
  return (
    <ModalPago
      plan={targetPlan}
      groupId={group.id}
      userId={currentUser?.uid}
      onClose={() => {
        setShowModalPago(false);
        setPagoAction(null);
      }}
      onSuccess={() => {
        setShowModalPago(false);
        setPagoAction(null);
        if (group.id) localStorage.setItem('activeGroupId', group.id);
        showToast('✅ ¡Plan activado! Recargando...');
        setTimeout(() => {
          window.location.replace('/admin');
        }, 1500);
      }}
    />
  );
})()}
    </div>
  );
}

// ── OVERVIEW TAB ──────────────────────────────────────────
// ── OVERVIEW TAB ──────────────────────────────────────────
function OverviewTab({
  group, members, plan, links, onDeleteGroup }) {
  const { t } = useTranslation();
  const usedLinks = links.filter(l => l.used).length;
  const pendingLinks = links.filter(l => !l.used).length;

  const stats = [
    { label: t('stat_participants').toUpperCase(), value: members.length, icon: '👥' },
    { label: t('stat_links_used').toUpperCase(), value: usedLinks, icon: '✅' },
    { label: t('stat_links_pending').toUpperCase(), value: pendingLinks, icon: '⏳' },
    { label: t('stat_plan_slots').toUpperCase(), value: plan.maxMembers, icon: '🎯' },
  ];

  // --- INICIO NUEVA FUNCIÓN PREDICCIONES ESPECIALES ---
  const MUNDIAL_LOCK = new Date('2026-06-11T17:00:00Z');
  const isLocked = group.locked || new Date() >= MUNDIAL_LOCK;
  const isPaidPlan = group.plan !== 'free'; // Verifica si NO es el plan gratis

  async function shareSpecialPredictions() {
    // Si aún no se cierra el mundial, mostramos el aviso y detenemos la función
    if (!isLocked) {
      alert("🔒 Esta función se habilitará apenas comience el Mundial y se cierren las predicciones especiales.");
      return;
    }

    try {
      const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));

      // Cargar predicciones especiales de cada miembro desde Firestore
      const snapshots = await Promise.all(
        sortedMembers.map(m =>
          getDoc(doc(db, 'specialPredictions', `${group.id}_${m.userId}`))
        )
      );

      let text = `⚽ *PITAZO MUNDIAL 2026*\n`;
      text += `🏆 *PREDICCIONES ESPECIALES*\n`;
      text += `📋 Grupo: *${group.name}*\n`;
      text += `━━━━━━━━━━━━━━━━━\n\n`;

      const GROUPS = ['A','B','C','D','E','F','G','H','I','J','K','L'];

      sortedMembers.forEach((m, i) => {
        text += `👤 *${m.name}*\n`;
        const snap = snapshots[i];
        if (snap.exists()) {
          const sp = snap.data().predictions || {};
          const hasPodio = sp.champion || sp.runnerUp || sp.thirdPlace || sp.fourthPlace || sp.topScorer;
          const hasGroups = GROUPS.some(g => sp[`group${g}_1`] || sp[`group${g}_2`]);

          if (hasPodio || hasGroups) {
            // Podio
            if (hasPodio) {
              if (sp.champion)    text += `🥇 Campeón: ${sp.champion.toUpperCase()}\n`;
              if (sp.runnerUp)    text += `🥈 Subcampeón: ${sp.runnerUp.toUpperCase()}\n`;
              if (sp.thirdPlace)  text += `🥉 Tercero: ${sp.thirdPlace.toUpperCase()}\n`;
              if (sp.fourthPlace) text += `🏅 Cuarto: ${sp.fourthPlace.toUpperCase()}\n`;
              if (sp.topScorer)   text += `🎯 Goleador: ${sp.topScorer}\n`;
            }
            // Clasificados por grupo
            if (hasGroups) {
              text += `📋 *Clasificados:*\n`;
              GROUPS.forEach(g => {
                const p1 = sp[`group${g}_1`];
                const p2 = sp[`group${g}_2`];
                if (p1 || p2) {
                  text += `  Grupo ${g}: ${p1 || '?'} / ${p2 || '?'}\n`;
                }
              });
            }
          } else {
            text += `_Sin predicciones especiales_\n`;
          }
        } else {
          text += `_Sin predicciones especiales_\n`;
        }
        text += `\n`;
      });

      text += `_pitazomundial.com_`;
      const encoded = encodeURIComponent(text);
      window.open(`https://wa.me/?text=${encoded}`, '_blank');

    } catch (e) {
      console.error('Error cargando predicciones especiales:', e);
      alert('❌ Error al cargar las predicciones. Intenta de nuevo.');
    }
  }
  // --- FIN NUEVA FUNCIÓN ---

  return (
    <div style={{ paddingBottom: 90 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {stats.map(s => (
          <Card key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 24, color: colors.gold }}>{s.value}</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B', letterSpacing: 1 }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {group.locked && (
        <div style={{ background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: colors.red }}>
          🔒 El torneo ya comenzó — las predicciones especiales están bloqueadas.
        </div>
      )}

      {/* --- INICIO NUEVO BOTÓN DE WHATSAPP --- */}
      {isPaidPlan && (
        <button onClick={shareSpecialPredictions}
          style={{
            marginTop: 12, width: '100%', padding: '12px',
            background: isLocked ? 'rgba(37,211,102,0.1)' : 'rgba(255,255,255,0.05)', 
            border: isLocked ? '1px solid rgba(37,211,102,0.3)' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 13,
            color: isLocked ? '#25D366' : '#8B8B9B', 
            cursor: 'pointer', letterSpacing: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8
          }}>
          {isLocked 
            ? '📲 COMPARTIR PREDICCIONES ESPECIALES' 
            : '🔒 COMPARTIR PREDICCIONES ESPECIALES (SE HABILITA AL INICIAR)'}
        </button>
      )}
      {/* --- FIN NUEVO BOTÓN --- */}

      {!group.locked && (
        <div style={{ background: 'rgba(240,180,41,0.06)', border: `1px solid rgba(240,180,41,0.15)`, borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: colors.gold, marginTop: 12 }}>
          <span dangerouslySetInnerHTML={{ __html: t('admin_tournament_starts') }} />
        </div>
      )}
      
      <JoinAsParticipantButton group={group} members={members} />

      {/* BOTÓN PARA ELIMINAR EL GRUPO */}
      <button
        onClick={onDeleteGroup}
        style={{
          marginTop: 32, width: '100%', padding: '12px',
          background: 'transparent', border: '1px solid rgba(230,57,70,0.3)',
          borderRadius: 10, fontFamily: "'Inter',sans-serif", fontSize: 13,
          color: '#E63946', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8
        }}
      >
        🗑️ ELIMINAR ESTE GRUPO
      </button>

    </div>
  );
}

// ── MEMBERS TAB ───────────────────────────────────────────
function MembersTab({
  members, loadingData, group }) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);

  if (loadingData) return <div style={{ textAlign: 'center', padding: 30, fontFamily: "'Inter',sans-serif", color: '#6B6B7B', fontSize: 13 }}>Cargando...</div>;

  if (members.length === 0) return (
    <Card style={{ textAlign: 'center', padding: 30 }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>👥</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B' }}>Aún no hay participantes. Genera links y compártelos.</div>
    </Card>
  );

  async function exportPDF() {
    setExporting(true);
    try {
      const [predsSnap, fixtSnap] = await Promise.all([
        getDocs(query(collection(db, 'predictions'), where('groupId', '==', group.id))),
        getDocs(collection(db, 'fixtures')),
      ]);
      const preds = predsSnap.docs.map(d => d.data());
      const fixtMap = {};
      fixtSnap.docs.forEach(d => { fixtMap[d.id] = d.data(); });

      const byUser = {};
      members.forEach(m => { byUser[m.userId] = { name: m.name, preds: [], points: m.totalPoints || 0 }; });
      preds.forEach(p => {
        if (byUser[p.userId]) byUser[p.userId].preds.push(p);
      });

      const now = new Date();
      let rows = '';
      const sortedMembers = [...members].sort((a,b) => (b.totalPoints||0)-(a.totalPoints||0));

      sortedMembers.forEach((m, mi) => {
        const userPreds = (byUser[m.userId]?.preds || []).sort((a,b) => {
          const fa = fixtMap[a.fixtureId]; const fb = fixtMap[b.fixtureId];
          return (fa?.matchNumber||0) - (fb?.matchNumber||0);
        });
        rows += `<tr style="background:${mi%2===0?'#f9f9f9':'#fff'}">
          <td style="padding:8px;font-weight:700;border:1px solid #ddd">#${mi+1} ${m.name}</td>
          ${userPreds.map(p => {
            const f = fixtMap[p.fixtureId];
            if (!f) return '<td style="border:1px solid #ddd;padding:6px;text-align:center">-</td>';
            const pts = p.points !== undefined ? `+${p.points}` : '?';
            const bg = p.points > 0 ? '#e6f9ee' : p.calculated ? '#fff0f0' : '#fffbe6';
            return `<td style="border:1px solid #ddd;padding:6px;text-align:center;background:${bg};font-size:12px">
              <div style="font-weight:600">${p.homeScore}-${p.awayScore}</div>
              <div style="font-size:10px;color:#666">${pts} pts</div>
            </td>`;
          }).join('')}
          <td style="padding:8px;font-weight:700;color:#C9820A;border:1px solid #ddd;text-align:center">${m.totalPoints||0} pts</td>
        </tr>`;
      });

      const fixtures = sortedMembers.length > 0
        ? (byUser[sortedMembers[0].userId]?.preds || []).map(p => fixtMap[p.fixtureId]).filter(Boolean).sort((a,b)=>(a.matchNumber||0)-(b.matchNumber||0))
        : [];

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
        <title>Predicciones - ${group.name}</title>
        <style>body{font-family:Arial,sans-serif;font-size:13px;} table{border-collapse:collapse;width:100%} h1{color:#080810} .header{background:#080810;color:#F0B429;padding:16px;border-radius:8px;margin-bottom:16px}</style>
      </head><body>
        <div class="header"><h2 style="margin:0">⚽ PITAZO MUNDIAL 2026</h2><div style="margin-top:4px;color:#aaa;font-size:12px">Grupo: ${group.name} · Exportado: ${now}</div></div>
        <table>
          <thead><tr style="background:#080810;color:#F0B429">
            <th style="padding:8px;border:1px solid #ddd;text-align:left">{t('label_participant')}</th>
            ${fixtures.map(f=>`<th style="padding:6px;border:1px solid #ddd;text-align:center;font-size:10px">${f.homeTeamName||''}<br>vs<br>${f.awayTeamName||''}</th>`).join('')}
            <th style="padding:8px;border:1px solid #ddd">TOTAL</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <p style="margin-top:16px;font-size:11px;color:#999">Generado automáticamente por Pitazo Mundial · ${now}</p>
      </body></html>`;

      const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform());

      if (isNative) {
        // En APK: construir texto plano y compartir por WhatsApp/otros
        const sortedMembers2 = [...members].sort((a,b) => (b.totalPoints||0)-(a.totalPoints||0));
        const medals = ['🥇','🥈','🥉'];
        const lines = sortedMembers2.map((m, i) => {
          const medal = medals[i] || `${i+1}.`;
          return `${medal} ${m.name} — ${m.totalPoints||0} pts (${m.exactPredictions||0} exactos)`;
        });
        const texto = `⚽ PITAZO MUNDIAL 2026\n🏆 ${group.name}\n📊 TABLA DE POSICIONES\n${'─'.repeat(25)}\n${lines.join('\n')}\n${'─'.repeat(25)}\nGenerado por pitazomundial.com`;

        if (navigator.share) {
          try {
            await navigator.share({ title: `Tabla - ${group.name}`, text: texto });
          } catch(e) {
            if (e.name !== 'AbortError') console.error('Share error:', e);
          }
        } else {
          // Fallback: copiar al portapapeles
          await navigator.clipboard.writeText(texto);
          alert('✅ Tabla copiada al portapapeles');
        }
      } else {
        // En web: descargar archivo HTML
        const blob = new Blob([html], { type: 'text/html' });
        const filename = `predicciones-${group.name.replace(/\s+/g,'-')}-${new Date().toISOString().slice(0,10)}.html`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch(e) { console.error(e); }
    setExporting(false);
  }

  function buildTableText() {
    const sortedM = [...members].sort((a,b) => (b.totalPoints||0)-(a.totalPoints||0));
    const medals = ['🥇','🥈','🥉'];
    let text = `⚽ *PITAZO MUNDIAL 2026*\n`;
    text += `🏆 *${group.name}*\n`;
    text += `📊 *TABLA DE POSICIONES*\n`;
    text += `━━━━━━━━━━━━━━━━━\n`;
    sortedM.forEach((m, i) => {
      const medal = medals[i] || `${i+1}.`;
      text += `${medal} *${m.name}*: ${m.totalPoints||0} pts`;
      if (m.exactPredictions) text += ` (${m.exactPredictions} exactos)`;
      text += `\n`;
    });
    text += `━━━━━━━━━━━━━━━━━\n`;
    text += `_Desempate por mayor cantidad de exactos_\n`;
    text += `_pitazomundial.com_`;
    return text;
  }

  function shareTableWhatsApp() {
    const text = buildTableText();
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform());
    if (isNative && navigator.share) {
      navigator.share({ title: `Tabla - ${group.name}`, text }).catch(e => { if (e.name !== 'AbortError') console.error(e); });
    } else {
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    }
  }

  function copyTableText() {
    const text = buildTableText();
    navigator.clipboard.writeText(text).then(() => {
      alert('✅ Tabla copiada al portapapeles');
    });
  }

  // --- FUNCIÓN PARA ELIMINAR PARTICIPANTE ---
  async function deleteParticipant(memberId, memberName) {
    if (memberId === group.adminId) {
      alert("No puedes eliminarte a ti mismo (Administrador).");
      return;
    }
    if (!window.confirm(`¿Seguro que deseas eliminar a ${memberName} de la polla? Perderá todos sus puntos y su cupo.`)) return;
    
    try {
      await deleteDoc(doc(db, 'groups', group.id, 'members', memberId));
      alert(`🗑️ ${memberName} ha sido eliminado del grupo.`);
      window.location.reload(); 
    } catch (error) {
      console.error(error);
      alert("Error al intentar eliminar al participante.");
    }
  }
  // --- FIN FUNCIÓN ELIMINAR ---

  return (
    <div style={{ paddingBottom: 90 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <SectionTitle>PARTICIPANTES ({members.length})</SectionTitle>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={shareTableWhatsApp}
            style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 8, padding: '7px 12px', fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#25D366', cursor: 'pointer', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 5 }}>
            📲 WHATSAPP
          </button>
          <button onClick={copyTableText}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${colors.border}`, borderRadius: 8, padding: '7px 12px', fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#aaa', cursor: 'pointer', letterSpacing: 1 }}>
            📋 COPIAR
          </button>
        </div>
      </div>
      {[...members].sort((a,b) => (b.totalPoints||0) - (a.totalPoints||0)).map((m, i) => {
        const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
        return (
        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: `1px solid ${colors.border}`, background: i === 0 ? 'rgba(240,180,41,0.04)' : 'transparent', borderRadius: i === 0 ? 8 : 0, paddingLeft: i === 0 ? 8 : 0 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: medal ? 'transparent' : '#1a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Oswald',sans-serif", fontSize: medal ? 22 : 14, color: colors.gold }}>{medal || (i + 1)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 1 }}>{m.name}</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>
              {m.email || t('admin_no_email')} · {m.exactPredictions||0} exactos · {m.correctPredictions||0} correctos
            </div>
          </div>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, color: (m.totalPoints||0) > 0 ? colors.gold : '#555' }}>
            {m.totalPoints || 0}<span style={{ fontSize: 10, color: '#555' }}> pts</span>
          </div>

          {/* --- INICIO NUEVO BOTÓN BASURA --- */}
          {m.userId !== group.adminId && (
            <button
              onClick={() => deleteParticipant(m.id, m.name)}
              style={{
                background: 'rgba(230,57,70,0.1)', border: '1px solid rgba(230,57,70,0.3)',
                borderRadius: 8, padding: '6px 8px', cursor: 'pointer', marginLeft: 10
              }}
              title="Eliminar participante"
            >
              🗑️
            </button>
          )}
          {/* --- FIN NUEVO BOTÓN BASURA --- */}

        </div>
        );
      })}
      {/* Nota desempate */}
      {members.length > 0 && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${colors.border}`, borderRadius: 10, fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#555', textAlign: 'center' }}>
          🏆 En caso de empate en puntos, gana quien tenga más predicciones exactas
        </div>
      )}
    </div>
  );
}

// ── LINKS TAB ─────────────────────────────────────────────
function LinksTab({
  group, links, plan, spotsLeft, onLinksGenerated, showToast }) {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [names, setNames] = useState(['']);
  const [showForm, setShowForm] = useState(false);

  async function generateLinks() {
    const validNames = names.filter(n => n.trim() !== '');
    if (validNames.length === 0) { showToast(t('toast_enter_name')); return; }
    if (validNames.length > spotsLeft) { showToast(`Solo quedan ${spotsLeft} cupos`); return; }

    setGenerating(true);
    try {
      for (const name of validNames) {
        await addDoc(collection(db, 'inviteLinks'), {
          groupId: group.id,
          participantName: name.trim(),
          email: '',
          used: false,
          createdAt: serverTimestamp(),
          expiresAt: null,
          createdBy: group.adminId
        });
      }
      setNames(['']);
      setShowForm(false);
      onLinksGenerated();
    } catch (e) { showToast(t('toast_error_generate_links')); }
    setGenerating(false);
  }

  const APP_URL = 'https://pitazomundial.com';

  function copyLink(linkId) {
    const url = `${APP_URL}/login/${linkId}`;
    navigator.clipboard.writeText(url);
    showToast(t('toast_link_copied'));
  }

  function copyAllPending() {
    const pending = links.filter(l => !l.used);
    const text = pending.map(l => `${l.participantName}: ${APP_URL}/login/${l.id}`).join('\n');
    navigator.clipboard.writeText(text);
    showToast(`📋 ${pending.length} links copiados`);
  }

  async function deleteLink(linkId, name) {
    if (!window.confirm(`¿Eliminar el link de ${name}? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteDoc(doc(db, 'inviteLinks', linkId));
      showToast(`🗑️ Link de ${name} eliminado`);
      onLinksGenerated();
    } catch(e) { showToast(t('toast_error_delete_link')); }
  }

  const pending = links.filter(l => !l.used);
  const used = links.filter(l => l.used);

  return (
    <div style={{ paddingBottom: 90 }}>
      {/* Generar nuevos links */}
      {spotsLeft > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 1, marginBottom: 12 }}>
            GENERAR LINKS DE INVITACIÓN
          </div>
          {!showForm ? (
            <button style={{ ...btn.gold, width: '100%' }} onClick={() => setShowForm(true)}>
              + GENERAR LINKS
            </button>
          ) : (
            <>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', marginBottom: 8 }}>
                Nombre de cada participante (un link por persona):
              </div>
              {names.map((name, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    value={name}
                    onChange={e => { const n = [...names]; n[i] = e.target.value; setNames(n); }}
                    placeholder={`Participante ${i + 1}`}
                    style={{ flex: 1, padding: '10px 12px', background: '#080810', border: `1px solid ${colors.border}`, borderRadius: 8, color: '#F5F5F0', fontFamily: "'Inter',sans-serif", fontSize: 14 }}
                  />
                  {names.length > 1 && (
                    <button style={btn.red} onClick={() => setNames(names.filter((_, j) => j !== i))}>✕</button>
                  )}
                </div>
              ))}
              {names.length < spotsLeft && (
                <button style={{ ...btn.outline, width: '100%', marginBottom: 10, fontSize: 12 }} onClick={() => setNames([...names, ''])}>
                  + Agregar otro
                </button>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...btn.gold, flex: 1 }} onClick={generateLinks} disabled={generating}>
                  {generating ? t('btn_generating') : t('btn_generate')}
                </button>
                <button style={btn.outline} onClick={() => setShowForm(false)}>CANCELAR</button>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Links pendientes */}
      {pending.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <SectionTitle>PENDIENTES ({pending.length})</SectionTitle>
            <button style={{ ...btn.outline, fontSize: 11, padding: '6px 12px' }} onClick={copyAllPending}>
              📋 COPIAR TODOS
            </button>
          </div>
          {pending.map(l => (
            <Card key={l.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 1, marginBottom: 2 }}>{l.participantName}</div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B' }}>⏳ Sin usar</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={{ ...btn.gold, padding: '8px 14px', fontSize: 12 }} onClick={() => copyLink(l.id)}>
                  📤 COPIAR
                </button>
                <button style={{ ...btn.red, padding: '8px 10px', fontSize: 12 }} onClick={() => deleteLink(l.id, l.participantName)}>
                  🗑️
                </button>
              </div>
            </Card>
          ))}
        </>
      )}

      {/* Links usados */}
      {used.length > 0 && (
        <>
          <SectionTitle>USADOS ({used.length})</SectionTitle>
          {used.map(l => (
            <Card key={l.id} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.green, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13 }}>{l.participantName}</div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B' }}>
                    ✅ {l.usedByEmail || 'Usado'} · {l.usedAt ? new Date(l.usedAt.toDate ? l.usedAt.toDate() : l.usedAt).toLocaleDateString('es-CO') : ''}
                  </div>
                </div>
                <button
                  style={{ ...btn.red, fontSize: 11, padding: '6px 10px', whiteSpace: 'nowrap' }}
                  onClick={async () => {
                    if (!window.confirm(`¿Resetear el link de ${l.participantName}? Podrán usarlo de nuevo.`)) return;
                    try {
                      await updateDoc(doc(db, 'inviteLinks', l.id), {
                        used: false,
                        usedBy: null,
                        usedByEmail: null,
                        usedAt: null
                      });
                      showToast(`🔄 Link de ${l.participantName} reseteado`);
                      onLinksGenerated();
                    } catch(e) { showToast(t('toast_error_reset_link')); }
                  }}
                >
                  🔄 RESETEAR
                </button>
              </div>
            </Card>
          ))}
        </>
      )}

      {links.length === 0 && !showForm && (
        <Card style={{ textAlign: 'center', padding: 30 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔗</div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B' }}>
            Genera links únicos para cada participante. Cada link solo puede usarse una vez.
          </div>
        </Card>
      )}
    </div>
  );
}

// ── POINTS TAB ────────────────────────────────────────────
function PointsTab({
  group, showToast, onRefresh }) {
  const { t } = useTranslation();
  const [rules, setRules] = useState(group.pointRules || DEFAULT_POINT_RULES);
  const [saving, setSaving] = useState(false);

  // ✅ Sincronizar rules cuando el grupo se actualiza desde Firestore
  useEffect(() => {
    if (group.pointRules) setRules(group.pointRules);
  }, [group.pointRules]);
  const MUNDIAL_LOCK = new Date('2026-06-11T17:00:00Z');
  const isFreePlan = group.plan === 'free' || group.pointRulesLocked === true;
  const timeLocked = group.locked || new Date() >= MUNDIAL_LOCK;
  const locked = timeLocked; // free ahora puede editar puntos básicos
  const hoursLeft = Math.max(0, Math.floor((MUNDIAL_LOCK - new Date()) / 3600000));
  const minutesLeft = Math.max(0, Math.floor(((MUNDIAL_LOCK - new Date()) % 3600000) / 60000));

  const visibleLabels = Object.entries(POINT_LABELS);
  const displayRules = rules; // todos editan sus propias reglas

  async function saveRules() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'groups', group.id), { pointRules: rules });
      // ✅ Actualizar el objeto group localmente para que el useEffect no revierta los cambios
      group.pointRules = { ...rules };
      playGolSound(0.5);
      showToast(t('toast_points_saved'));
      onRefresh();
    } catch (e) { showToast(t('toast_error_save')); }
    setSaving(false);
  }

  return (
    <div style={{ paddingBottom: 90 }}>
      {timeLocked ? (
        <div style={{ background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.2)', borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: colors.red, marginBottom: 12 }}>
          🔒 Configuración bloqueada. El torneo ya comenzó o está a menos de 2 horas de empezar.
        </div>
      ) : (
        <div style={{ background: 'rgba(240,180,41,0.06)', border: `1px solid rgba(240,180,41,0.15)`, borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: colors.gold, marginBottom: 12 }}>
          ⏱ Tienes <strong>{hoursLeft}h {minutesLeft}m</strong> para modificar los puntos antes del inicio del Mundial.
        </div>
      )}

      {/* Criterios básicos */}
      <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#6B6B7B', letterSpacing: 1, marginBottom: 8, marginTop: 4 }}>CRITERIOS POR PARTIDO</div>
      {visibleLabels.filter(([k, v]) => v.planFree).map(([k, v]) => (
        <Card key={k} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1 }}>{v.icon} {v.label}</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, color: colors.gold }}>{displayRules[k] ?? 0} pts</div>
          </div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#555', marginBottom: locked ? 0 : 8 }}>{v.desc}</div>
          {!locked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setRules(r => ({ ...r, [k]: Math.max(0, (r[k]??0) - 1) }))}
                style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', border: `1px solid ${colors.border}`, color: '#F5F5F0', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
              <input type="range" min={0} max={20} value={rules[k]??0} onChange={e => setRules(r => ({ ...r, [k]: parseInt(e.target.value) }))}
                style={{ flex: 1, accentColor: colors.gold }} />
              <button onClick={() => setRules(r => ({ ...r, [k]: Math.min(20, (r[k]??0) + 1) }))}
                style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', border: `1px solid ${colors.border}`, color: '#F5F5F0', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            </div>
          )}
        </Card>
      ))}

      {/* Predicciones especiales */}
      <>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#6B6B7B', letterSpacing: 1, marginBottom: 8, marginTop: 16 }}>
          PREDICCIONES ESPECIALES (antes del Mundial)
          {isFreePlan && <span style={{ color: '#a78bfa', marginLeft: 8, fontSize: 10 }}>— Plan de pago</span>}
        </div>

        {visibleLabels.filter(([k, v]) => !v.planFree && k !== 'nextRound').map(([k, v]) => (
          <Card key={k} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1 }}>{v.icon} {v.label}</div>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, color: colors.gold }}>{displayRules[k] ?? 0} pts</div>
            </div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#555', marginBottom: (timeLocked || isFreePlan) ? 0 : 8 }}>{v.desc}</div>
            {!timeLocked && !isFreePlan && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setRules(r => ({ ...r, [k]: Math.max(0, (r[k]??0) - 5) }))}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', border: `1px solid ${colors.border}`, color: '#F5F5F0', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <input type="number" min={0} max={999} value={rules[k]??0}
                  onChange={e => setRules(r => ({ ...r, [k]: Math.min(999, Math.max(0, parseInt(e.target.value)||0)) }))}
                  style={{ flex: 1, padding: '6px 10px', background: '#080810', border: `1px solid ${colors.border}`, borderRadius: 8, color: '#F5F5F0', fontFamily: "'Oswald',sans-serif", fontSize: 18, textAlign: 'center', accentColor: colors.gold }} />
                <button onClick={() => setRules(r => ({ ...r, [k]: Math.min(999, (r[k]??0) + 5) }))}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', border: `1px solid ${colors.border}`, color: '#F5F5F0', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              </div>
            )}
          </Card>
        ))}

        {/* Clasificados por grupo */}
        {visibleLabels.filter(([k, v]) => k === 'nextRound').map(([k, v]) => {
          const pts = displayRules[k] ?? 0;
          const total = 24;
          const half = Math.round(total / 2);
          return (
            <Card key={k} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1 }}>{v.icon} {v.label}</div>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 20, color: colors.gold }}>{pts} pts c/u</div>
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#555', marginBottom: 8 }}>
                {v.desc} — puntuación proporcional a los aciertos
              </div>
              {pts > 0 && (
                <div style={{ background: 'rgba(240,180,41,0.04)', border: '1px solid rgba(240,180,41,0.1)', borderRadius: 8, padding: '8px 10px', marginBottom: (timeLocked || isFreePlan) ? 0 : 10, fontSize: 11, color: '#888', fontFamily: "'Inter',sans-serif", lineHeight: 1.6 }}>
                  💡 <strong style={{color: colors.gold}}>Ejemplo:</strong> Si un participante acierta <strong style={{color:'#fff'}}>todos ({total}/{total})</strong> → <strong style={{color: colors.gold}}>{pts * total} pts</strong>.
                  Si acierta <strong style={{color:'#fff'}}>la mitad ({half}/{total})</strong> → <strong style={{color: colors.gold}}>{pts * half} pts</strong>.
                </div>
              )}
              {!timeLocked && !isFreePlan && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setRules(r => ({ ...r, [k]: Math.max(0, (r[k]??0) - 5) }))}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', border: `1px solid ${colors.border}`, color: '#F5F5F0', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <input type="number" min={0} max={999} value={rules[k]??0}
                    onChange={e => setRules(r => ({ ...r, [k]: Math.min(999, Math.max(0, parseInt(e.target.value)||0)) }))}
                    style={{ flex: 1, padding: '6px 10px', background: '#080810', border: `1px solid ${colors.border}`, borderRadius: 8, color: '#F5F5F0', fontFamily: "'Oswald',sans-serif", fontSize: 18, textAlign: 'center' }} />
                  <button onClick={() => setRules(r => ({ ...r, [k]: Math.min(999, (r[k]??0) + 5) }))}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', border: `1px solid ${colors.border}`, color: '#F5F5F0', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              )}
            </Card>
          );
        })}
      </>

      {!locked && (
        <button style={{ ...btn.gold, width: '100%', padding: 14, fontSize: 15, marginTop: 8 }} onClick={saveRules} disabled={saving}>
          {saving ? t('btn_saving') : t('btn_save_config')}
        </button>
      )}
    </div>
  );
}

// ── RESULTS TAB ───────────────────────────────────────────
function ResultsTab({
  group, members, showToast }) {
  const { t } = useTranslation();
  const [fixtures, setFixtures] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState('Grupos');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [homeVal, setHomeVal] = useState('');
  const [awayVal, setAwayVal] = useState('');

  const PHASE_ORDER = ['Grupos','16avos','Octavos','Cuartos','Semifinal','Semifinal Final','Tercer puesto','Final'];

  useEffect(() => { loadFixtures(); }, []);

  async function loadFixtures() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'fixtures'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0));
      setFixtures(list);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function startEdit(fixture) {
    setEditingId(fixture.id);
    setHomeVal(fixture.homeScore !== null && fixture.homeScore !== undefined ? String(fixture.homeScore) : '');
    setAwayVal(fixture.awayScore !== null && fixture.awayScore !== undefined ? String(fixture.awayScore) : '');
  }

  async function saveResult(fixture) {
    if (homeVal === '' || awayVal === '') return;
    const h = parseInt(homeVal);
    const a = parseInt(awayVal);
    setSaving(fixture.id);
    try {
      await updateDoc(doc(db, 'fixtures', fixture.id), {
        homeScore: h,
        awayScore: a,
        status: 'finished',
        updatedAt: serverTimestamp(),
      });

      const rules = group.pointRules || { exactScore: 5, winner: 1, goalDiff: 2, exactGoalsPerTeam: 2 };
      const predsQ = query(collection(db, 'predictions'), where('fixtureId', '==', fixture.id), where('groupId', '==', group.id));
      const predsSnap = await getDocs(predsQ);

      // Calcular puntos de cada predicción y acumular deltas por miembro
      const predUpdates = [];
      const memberDeltas = {}; // key: userId

      for (const predDoc of predsSnap.docs) {
        const pred = predDoc.data();
        const ph = pred.homeScore;
        const pa = pred.awayScore;
        let newPts = 0;
        const breakdown = [];
        const realWinner = h > a ? 'home' : a > h ? 'away' : 'draw';
        const predWinner = ph > pa ? 'home' : pa > ph ? 'away' : 'draw';

        if (realWinner === predWinner) {
          const p = rules.winner || 1;
          newPts += p;
          breakdown.push({ key: 'winner', label: t('score_winner'), pts: p });
        }
        if ((h - a) === (ph - pa)) {
          const p = rules.goalDiff || 2;
          newPts += p;
          breakdown.push({ key: 'goalDiff', label: t('score_goaldiff'), pts: p });
        }
        if (ph === h || pa === a) {
          const p = rules.exactGoalsPerTeam || 2;
          newPts += p;
          breakdown.push({ key: 'exactGoals', label: t('score_exactgoals'), pts: p });
        }
        if (ph === h && pa === a) {
          const p = rules.exactScore || 5;
          newPts += p;
          breakdown.push({ key: 'exactScore', label: t('score_exact'), pts: p });
        }
        if (newPts === 0) breakdown.push({ key: 'none', label: '❌ Sin aciertos', pts: 0 });

        const oldPts = pred.points || 0;
        const oldExact = pred.breakdown?.some(b => b.key === 'exactScore') ? 1 : 0;
        const ptsDelta = newPts - oldPts;
        const exactDelta = (ph === h && pa === a ? 1 : 0) - oldExact;
        const correctDelta = (newPts > 0 ? 1 : 0) - (oldPts > 0 ? 1 : 0);

        predUpdates.push({ ref: doc(db, 'predictions', predDoc.id), data: { points: newPts, calculated: true, breakdown, updatedAt: serverTimestamp() } });

        if (!memberDeltas[pred.userId]) {
          memberDeltas[pred.userId] = { ref: doc(db, 'groups', group.id, 'members', pred.userId), ptsDelta: 0, exactDelta: 0, correctDelta: 0 };
        }
        memberDeltas[pred.userId].ptsDelta += ptsDelta;
        memberDeltas[pred.userId].exactDelta += exactDelta;
        memberDeltas[pred.userId].correctDelta += correctDelta;
      }

      // Leer miembros actuales de Firestore y preparar batch
      const batch = writeBatch(db);
      for (const u of predUpdates) batch.update(u.ref, u.data);

      for (const uid of Object.keys(memberDeltas)) {
        const delta = memberDeltas[uid];
        const memberSnap = await getDoc(delta.ref);
        if (memberSnap.exists()) {
          const m = memberSnap.data();
          batch.update(delta.ref, {
            totalPoints: Math.max(0, (m.totalPoints || 0) + delta.ptsDelta),
            exactPredictions: Math.max(0, (m.exactPredictions || 0) + delta.exactDelta),
            correctPredictions: Math.max(0, (m.correctPredictions || 0) + delta.correctDelta),
            updatedAt: serverTimestamp()
          });
        }
      }

      await batch.commit();

      setFixtures(prev => prev.map(f => f.id === fixture.id ? { ...f, homeScore: h, awayScore: a, status: 'finished' } : f));
      setEditingId(null);
      playGolSound(0.8);
      showToast(t('toast_result_saved'));
    } catch (e) {
      console.error(e);
      showToast(t('toast_error_save_result'));
    }
    setSaving(null);
  }

  async function clearResult(fixture) {
    setSaving(fixture.id);
    try {
      await updateDoc(doc(db, 'fixtures', fixture.id), {
        homeScore: null, awayScore: null, status: 'upcoming', updatedAt: serverTimestamp(),
      });
      setFixtures(prev => prev.map(f => f.id === fixture.id ? { ...f, homeScore: null, awayScore: null, status: 'upcoming' } : f));
      showToast(t('toast_result_deleted'));
    } catch (e) { showToast(t('toast_error_delete_result')); }
    setSaving(null);
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40, fontFamily: "'Inter',sans-serif", color: '#6B6B7B', fontSize: 13 }}>Cargando partidos...</div>;

  const phases = [...new Set(fixtures.map(f => f.phase))].sort((a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b));
  const filtered = fixtures.filter(f => f.phase === phaseFilter);

  return (
    <div>
      <div style={{ background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.15)', borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: colors.gold, marginBottom: 12 }}>
        {t('admin_enter_results')}
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', overflowY: 'hidden', padding: '4px 0 12px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {phases.map(p => (
          <button key={p} onClick={() => setPhaseFilter(p)}
            style={{ padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: "'Oswald',sans-serif", fontSize: 11, letterSpacing: 1, whiteSpace: 'nowrap', flexShrink: 0, background: phaseFilter === p ? colors.gold : '#1a1a2a', color: phaseFilter === p ? '#080810' : '#6B6B7B' }}>
            {p.toUpperCase()}
          </button>
        ))}
      </div>

      {filtered.map(fixture => {
        const hasResult = fixture.homeScore !== null && fixture.homeScore !== undefined;
        const isEditing = editingId === fixture.id;
        const isSaving = saving === fixture.id;

        return (
          <Card key={fixture.id} style={{ marginBottom: 8, border: `1px solid ${hasResult ? 'rgba(6,184,106,0.25)' : colors.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B' }}>
                #{fixture.matchNumber} · {fixture.date} · {fixture.time}
              </div>
              {hasResult && !isEditing && (
                <div style={{ fontSize: 10, fontFamily: "'Oswald',sans-serif", color: colors.green, background: 'rgba(6,184,106,0.1)', padding: '2px 8px', borderRadius: 20 }}>
                  ✓ REGISTRADO
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, textAlign: 'center', fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1 }}>
                {fixture.homeTeamName}
              </div>

              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <input type="number" min={0} max={20} value={homeVal} onChange={e => setHomeVal(e.target.value)}
                    style={{ width: 44, height: 40, textAlign: 'center', background: '#080810', border: `1px solid ${colors.gold}`, borderRadius: 8, color: colors.gold, fontFamily: "'Oswald',sans-serif", fontSize: 20 }} />
                  <span style={{ color: '#555', fontFamily: "'Oswald',sans-serif" }}>-</span>
                  <input type="number" min={0} max={20} value={awayVal} onChange={e => setAwayVal(e.target.value)}
                    style={{ width: 44, height: 40, textAlign: 'center', background: '#080810', border: `1px solid ${colors.gold}`, borderRadius: 8, color: colors.gold, fontFamily: "'Oswald',sans-serif", fontSize: 20 }} />
                </div>
              ) : (
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, letterSpacing: 3, color: hasResult ? colors.green : '#333', minWidth: 70, textAlign: 'center' }}>
                  {hasResult ? `${fixture.homeScore}-${fixture.awayScore}` : '?-?'}
                </div>
              )}

              <div style={{ flex: 1, textAlign: 'center', fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1 }}>
                {fixture.awayTeamName}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'center' }}>
              {isEditing ? (
                <>
                  <button onClick={() => saveResult(fixture)} disabled={isSaving || homeVal === '' || awayVal === ''}
                    style={{ ...btn.gold, padding: '8px 20px', fontSize: 12, opacity: (isSaving || homeVal === '' || awayVal === '') ? 0.5 : 1 }}>
                    {isSaving ? '...' : '✓ GUARDAR'}
                  </button>
                  <button onClick={() => setEditingId(null)} style={{ ...btn.outline, padding: '8px 14px', fontSize: 12 }}>
                    CANCELAR
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => startEdit(fixture)} style={{ ...btn.outline, padding: '7px 16px', fontSize: 11 }}>
                    {hasResult ? t('btn_edit_result') : t('btn_add_result')}
                  </button>
                  {hasResult && (
                    <button onClick={() => clearResult(fixture)} disabled={isSaving}
                      style={{ ...btn.red, fontSize: 11 }}>
                      🗑 BORRAR
                    </button>
                  )}
                </>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── JOIN AS PARTICIPANT BUTTON ────────────────────────────
function JoinAsParticipantButton({ group, members }) {
  const { t } = useTranslation();
  const { currentUser, userData, refreshUserData } = useAuth();
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  const alreadyMember = members.some(m => m.userId === currentUser?.uid);

  useEffect(() => { setJoined(alreadyMember); }, [alreadyMember]);

  async function joinGroup() {
    if (!currentUser || joining || joined) return;
    setJoining(true);
    try {
      const memberData = {
        userId: currentUser.uid,
        name: userData?.name || currentUser.displayName || 'Admin',
        email: userData?.email || currentUser.email || '',
        joinedAt: serverTimestamp(),
        inviteLinkId: 'admin-self-join',
        totalPoints: 0,
        exactPredictions: 0,
        correctPredictions: 0,
        isAdmin: true,
      };
      await setDoc(doc(db, 'groups', group.id, 'members', currentUser.uid), memberData);
      await updateDoc(doc(db, 'groups', group.id), {
        currentMembers: (group.currentMembers || 0) + 1
      });
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const existingIds = userSnap.exists() ? (userSnap.data().groupIds || (userSnap.data().groupId ? [userSnap.data().groupId] : [])) : [];
      const newGroupIds = existingIds.includes(group.id) ? existingIds : [...existingIds, group.id];
      await updateDoc(doc(db, 'users', currentUser.uid), { groupId: group.id, groupIds: newGroupIds });
      await refreshUserData();
      setJoined(true);
    } catch (e) { console.error(e); }
    setJoining(false);
  }

  if (joined) return (
    <div style={{ marginTop: 12, background: 'rgba(6,184,106,0.08)', border: '1px solid rgba(6,184,106,0.2)', borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#06B86A', textAlign: 'center' }}>
      ✓ Ya eres participante de este grupo
    </div>
  );

  return (
    <button onClick={joinGroup} disabled={joining}
      style={{ marginTop: 12, width: '100%', padding: '12px', background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.3)', borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: '#F0B429', cursor: 'pointer' }}>
      {joining ? t('btn_joining') : t('btn_join_participant')}
    </button>
  );
}

// ── MATCHES ADMIN TAB ─────────────────────────────────────
function MatchesAdminTab({ group, members, showToast }) {
  const { t } = useTranslation();
  const [fixtures, setFixtures] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState('Grupos');

  const PHASE_ORDER = ['Grupos','16avos','Octavos','Cuartos','Semifinal','Semifinal Final','Tercer puesto','Final'];

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [fixtSnap, predsSnap] = await Promise.all([
        getDocs(collection(db, 'fixtures')),
        getDocs(query(collection(db, 'predictions'), where('groupId', '==', group.id))),
      ]);
      const fixtList = fixtSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.matchNumber || 0) - (b.matchNumber || 0));
      setFixtures(fixtList);
      setPredictions(predsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  function shareWhatsApp(fixture) {
    const matchPreds = predictions.filter(p => p.fixtureId === fixture.id);
    const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));

    let text = `⚽ *PITAZO MUNDIAL 2026*\n`;
    text += `📋 *${group.name}*\n`;
    text += `━━━━━━━━━━━━━━━━━\n`;
    text += `🏟 *${fixture.homeTeamName} vs ${fixture.awayTeamName}*\n`;
    text += `📅 ${fixture.date} · ${fixture.time}\n`;
    text += `━━━━━━━━━━━━━━━━━\n\n`;

    sortedMembers.forEach((m, i) => {
      const pred = matchPreds.find(p => p.userId === m.userId);
      if (pred) {
        text += `${i + 1}. *${m.name}*: ${pred.homeScore} - ${pred.awayScore}\n`;
      } else {
        text += `${i + 1}. *${m.name}*: _(sin predicción)_\n`;
      }
    });

    text += `\n_Generado por Pitazo Mundial_`;
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  }

  function copyText(fixture) {
    const matchPreds = predictions.filter(p => p.fixtureId === fixture.id);
    const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));

    let text = `⚽ PITAZO MUNDIAL 2026 · ${group.name}\n`;
    text += `${fixture.homeTeamName} vs ${fixture.awayTeamName} · ${fixture.date} ${fixture.time}\n\n`;
    sortedMembers.forEach((m, i) => {
      const pred = matchPreds.find(p => p.userId === m.userId);
      text += pred ? `${i+1}. ${m.name}: ${pred.homeScore}-${pred.awayScore}\n` : `${i+1}. ${m.name}: sin predicción\n`;
    });
    navigator.clipboard.writeText(text);
    showToast(t('toast_copied'));
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40, fontFamily: "'Inter',sans-serif", color: '#6B6B7B', fontSize: 13 }}>Cargando partidos...</div>;

  const phases = [...new Set(fixtures.map(f => f.phase))].sort((a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b));
  const filtered = fixtures.filter(f => f.phase === phaseFilter);

  if (selectedFixture) {
    const matchPreds = predictions.filter(p => p.fixtureId === selectedFixture.id);
    const sortedMembers = [...members].sort((a, b) => a.name.localeCompare(b.name));
    const hasResult = selectedFixture.homeScore !== null && selectedFixture.homeScore !== undefined;

    return (
      <div style={{ animation: 'fadeIn .2s ease', paddingBottom: 90 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => setSelectedFixture(null)}
            style={{ background: '#1a1a2a', border: `1px solid ${colors.border}`, borderRadius: 8, padding: '7px 12px', color: '#F5F5F0', cursor: 'pointer', fontFamily: "'Oswald',sans-serif", fontSize: 12 }}>
            ← VOLVER
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 1 }}>
              {selectedFixture.homeTeamName} vs {selectedFixture.awayTeamName}
            </div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B' }}>
              #{selectedFixture.matchNumber} · {selectedFixture.date} · {selectedFixture.time}
            </div>
          </div>
        </div>

        {hasResult && (
          <div style={{ background: 'rgba(6,184,106,0.08)', border: '1px solid rgba(6,184,106,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, textAlign: 'center' }}>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B', marginBottom: 2 }}>RESULTADO FINAL</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 28, color: colors.green, letterSpacing: 4 }}>
              {selectedFixture.homeScore} - {selectedFixture.awayScore}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          {sortedMembers.map((m, i) => {
            const pred = matchPreds.find(p => p.userId === m.userId);
            const hit = pred?.calculated && pred?.points > 0;
            return (
              <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', marginBottom: 6, borderRadius: 12, background: colors.card, border: `1px solid ${hit ? 'rgba(6,184,106,0.2)' : colors.border}`, overflow: 'hidden' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Oswald',sans-serif", fontSize: 12, color: colors.gold, flexShrink: 0 }}>{i+1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                  {pred?.calculated && (
                    <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: hit ? colors.green : '#555', marginTop: 1 }}>
                      {pred.points > 0 ? `+${pred.points} pts` : '0 pts'}
                    </div>
                  )}
                </div>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, letterSpacing: 1, color: pred ? colors.gold : '#333', flexShrink: 0 }}>
                  {pred ? `${pred.homeScore}-${pred.awayScore}` : '—'}
                </div>
                {hit && <div style={{ fontSize: 14, flexShrink: 0 }}>✅</div>}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => shareWhatsApp(selectedFixture)}
            style={{ flex: 1, padding: '12px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, color: '#25D366', cursor: 'pointer' }}>
            📲 COMPARTIR WHATSAPP
          </button>
          <button onClick={() => copyText(selectedFixture)}
            style={{ padding: '12px 16px', background: '#1a1a2a', border: `1px solid ${colors.border}`, borderRadius: 10, fontFamily: "'Oswald',sans-serif", fontSize: 12, color: '#F5F5F0', cursor: 'pointer' }}>
            📋
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 90 }}>
      <div style={{ background: 'rgba(240,180,41,0.06)', border: `1px solid rgba(240,180,41,0.15)`, borderRadius: 8, padding: '10px 14px', fontFamily: "'Inter',sans-serif", fontSize: 12, color: colors.gold, marginBottom: 12 }}>
        {t('admin_tap_match')}
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', overflowY: 'hidden', padding: '4px 0 12px', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
        {phases.map(p => (
          <button key={p} onClick={() => setPhaseFilter(p)}
            style={{ padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: "'Oswald',sans-serif", fontSize: 11, letterSpacing: 1, whiteSpace: 'nowrap', flexShrink: 0, background: phaseFilter === p ? colors.gold : '#1a1a2a', color: phaseFilter === p ? '#080810' : '#6B6B7B' }}>
            {p.toUpperCase()}
          </button>
        ))}
      </div>

      {filtered.map(fixture => {
        const matchPreds = predictions.filter(p => p.fixtureId === fixture.id);
        const hasResult = fixture.homeScore !== null && fixture.homeScore !== undefined;
        const predCount = matchPreds.length;

        return (
          <div key={fixture.id} onClick={() => setSelectedFixture(fixture)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', marginBottom: 8, borderRadius: 12, background: colors.card, border: `1px solid ${hasResult ? 'rgba(6,184,106,0.2)' : colors.border}`, cursor: 'pointer', overflow: 'hidden' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, letterSpacing: 1, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fixture.homeTeamName} vs {fixture.awayTeamName}
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 10, color: '#6B6B7B' }}>
                {fixture.date} · {fixture.time} · {predCount}/{members.length} predicciones
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              {hasResult ? (
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: colors.green }}>{fixture.homeScore}-{fixture.awayScore}</div>
              ) : (
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: '#333' }}>?-?</div>
              )}
              <div style={{ fontSize: 9, color: predCount === members.length ? colors.green : '#6B6B7B', marginTop: 2 }}>
                {predCount === members.length ? '✓ completo' : `${predCount}/${members.length}`}
              </div>
            </div>
            <div style={{ color: '#6B6B7B', fontSize: 14, flexShrink: 0 }}>›</div>
          </div>
        );
      })}
    </div>
  );
}